import React, {useEffect, useRef, useState} from "react";
import { makeStyles } from '@material-ui/core/styles';
import {AppBar, Dialog, DialogContent, Drawer, Grid, useTheme} from "@material-ui/core";
import Launchpad from "../../component/Launchpad";
import {useDispatch, useSelector} from "../../store";
import {selectApplets, selectUserInfo} from "../../reducer/iam/authReducer";
import { useHistory } from "react-router-dom";
import Button from "../../lib/appfuse-react/component/Button";
import Space from "../../lib/appfuse-react/component/Space";
import Typography from "@material-ui/core/Typography";
import SearchField from "../../component/SearchField";
import Expander from "../../lib/appfuse-react/component/Expander";
import Toolbar from "../../component/Toolbar";
import UserProfile from "./UserProfile";
import lang from "../../lib/appfuse-react/core/lang";
import {selectTitle} from "../../reducer/app/appReducer";
import i18n from "../../lib/appfuse-react/core/i18n";
import Box from "../../lib/appfuse-react/component/Box";
import env from "../../lib/appfuse-react/core/env";
import useRouter from "../../lib/appfuse-react/hook/useRouter";
import Image from "../../lib/appfuse-react/component/Image";
import authService from "../../service/iam/authService";
import usePrompt from "../../lib/appfuse-react/hook/usePrompt";
import Form from "../../lib/appfuse-react/component/Form";
import IconButton from "@material-ui/core/IconButton";
import {Visibility, VisibilityOff} from "@material-ui/icons";
import UtilService from "../../service/util/utilService";
import {selectAllCount, setAllCount} from "../../reducer/inboxCount/inboxCountReducer";
import TextField from "../../lib/appfuse-react/component/TextField";

const initialContext = {
    workspace: undefined
}

const WorkbenchContext = React.createContext(initialContext);

const useStyles = makeStyles((theme) => ({
    appBar: {
        zIndex: theme.zIndex.drawer + 100,
        backgroundColor: theme.brand.appbar?.backgroundColor,
        color: theme.brand.appbar?.color,
    },
    drawer: {
        flexShrink: 0
    },
    drawerPaper: {
        top: theme.spacing(8),
        height: "100%",
        backgroundColor: 'rgba(255, 255, 255, 0.75)'
    }
}));

function Workbench(props) {
    const dispatch = useDispatch();
    const theme = useTheme();
    const router = useRouter();
    const backgroundImage = router.resolve(theme.brand.backgroundImage);
    const classes = useStyles({backgroundImage});
    const history = useHistory();
    const [keyword, setKeyword] = useState(null);
    const [launcherOpen, setLauncherOpen] = useState(false);
    const [userProfileAnchorEl, setUserProfileAnchorEl] = useState(null);
    const [changePasswordOpen, setChangePasswordOpen] = useState(false);
    const title = useSelector(selectTitle);
    const userInfo = useSelector(selectUserInfo);
    const applets = useSelector(selectApplets);
    const allCount = useSelector(selectAllCount);
    const responsive = env.responsive;
    const workspaceRef = useRef();
    const workspace = workspaceRef?.current;

    useEffect(() => {
        (async ()=> {
            const countData = await UtilService.getByUrl('inboxes/office/myTasks/count');
            dispatch(setAllCount(countData));
        })();
    }, [])

    const handleToggleLauncher = () => setLauncherOpen(!launcherOpen);
    const handleCloseLauncher = () => setLauncherOpen(false);

    const handleLaunchpadClick = (event) => {
        const { path } = event;
        history.push(`/${path}`);
    };

    const handleToggleUserProfile = (event) => {
        setUserProfileAnchorEl(userProfileAnchorEl ? null : event.currentTarget);
    };

    const handleUserProfileClose = () => {
        setUserProfileAnchorEl(null);
    }

    const handleChangePassword = async (value) => {
        await authService.changePassword(value);
    }

    const handleGotoInbox = async () => {
        const countData = await UtilService.getByUrl('inboxes/office/myTasks/count');
        dispatch(setAllCount(countData));
        history.push('/inbox');
    }

    const handleLogout = () => {
        setUserProfileAnchorEl(null);
        authService.logout();
        router.reload();
    }

    const handleSearch = event => {
        const keyword = event.value;
        setKeyword(keyword);
    }

    window.document.title = i18n.translate(title);

    const settings = env.get('app');

    const stageInfo = env.appStage.toUpperCase() === 'PROD' ? null : env.appStage;
    const versionInfo = env.appVersion + `${stageInfo ? '@' + stageInfo: ''}`;
    return (
        <Box className="app-workbench" fullWidth fullHeight overflow="hidden" display="flex" flexDirection="column">
            <AppBar position="static" className={classes.appBar}>
                <Toolbar>
                    <Image value={theme.brand.logo} onClick={()=> history.push('/main')} height={32} title={versionInfo} />
                    <Space spacing={1} />
                    <Typography variant="h5" noWrap>
                        {i18n.translate(title)}
                        <Space spacing={1} />
                        {stageInfo ? <Typography variant="body2" display="inline">{versionInfo}</Typography> : null}
                    </Typography>
                    {stageInfo === 'UAT' && <Space spacing={1} />}
                    {stageInfo === 'UAT' && <Typography variant="h3" style={{color:'red'}} noWrap>{i18n.translate('Text Environment')}</Typography>}
                    <Box display={lang.isFalse(settings.search) ? 'none' : 'initial'}>
                        <SearchField label="Search" value={keyword} onChange={handleSearch} />
                    </Box>
                    <Expander />
                    <Typography variant="subtitle1" noWrap>{`${i18n.translate('Welcome')} ${userInfo?.name || ''}`}</Typography>
                    <Space spacing={2} />
                    <Button icon="mail" variant="icon" badge={allCount.inboxCount}  color="inherit" onClick={handleGotoInbox}/>
                    <Button icon="account_circle" variant="icon"  color="inherit" onClick={handleToggleUserProfile} />
                    <Button icon="apps" variant="icon" onClick={handleToggleLauncher}  color="inherit" />
                </Toolbar>
            </AppBar>
            <Box fullWidth fullHeight backgroundImage={backgroundImage} overflow="auto">
                <Box ref={workspaceRef} fullHeight fullWidth overflow="hidden" minWidth={`${responsive.minWidth}px`}>
                    <WorkbenchContext.Provider value={{workspace}}>
                        {props.children}
                    </WorkbenchContext.Provider>
                </Box>
            </Box>
            <Drawer className={classes.drawer}
                    classes={{ paper: classes.drawerPaper }}
                    anchor="right"
                    open={launcherOpen}
                    onClose={handleCloseLauncher}
                    PaperProps={{ style: { height: `calc(100% - 64px)`}}} >
                <Launchpad data={applets} onClick={handleLaunchpadClick} />
            </Drawer>
            <UserProfile anchorEl={userProfileAnchorEl} userInfo={userInfo}
                         open={lang.isNotNullOrUndefined(userProfileAnchorEl)}
                         onClose={handleUserProfileClose} onChangePassword={() => setChangePasswordOpen(true)} onLogout={handleLogout}/>
            {changePasswordOpen && <ChangePassword open={changePasswordOpen} onClose={() => setChangePasswordOpen(false)} onApply={handleChangePassword}/>}
        </Box>
    )
}

function ChangePassword(props) {
    const prompt = usePrompt();
    const {open} = props;
    const [value, setValue] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleChange = (event) => {
        const {value} = event;
        setValue(value);
    };

    const handleOK = async () => {
        if(lang.isEmpty(value?.newPassword)) {
            prompt.confirm('Please enter the new password.');
            return;
        } else if(lang.isEmpty(value?.confirmNewPassword)) {
            prompt.confirm('Please enter the new password again.');
            return;
        }
        try {
            await props.onApply(value);
            prompt.success('Password changed successfully.');
            props.onClose();
        } catch (e) {
            prompt.alert(e, {title: 'Failed to change password.'});
        }
    };

    return (
        <Dialog open={open} onClose={props.onClose} maxWidth='xs' fullWidth>
            <Box paddingTop={2}>
                <Typography align='center' variant='h6'>{i18n.translate('Change Password')}</Typography>
            </Box>
            <DialogContent>
                <Box fullHeight fullWidth paddingBottom={3} paddingX={2}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography variant='caption' color='textSecondary'>{i18n.translate('Please contain at least 8 characters, including English uppercase and lowercase letters, numbers and symbols')}</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Form value={value} onChange={handleChange}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        {/*新密碼*/}
                                        <TextField name='newPassword' label='Please enter the new password.' variant='outlined' fullWidth required type={showPassword? "text": "password"}
                                                   InputProps={{
                                                       endAdornment:
                                                           <IconButton
                                                               aria-label="toggle password visibility"
                                                               onClick={() => setShowPassword(!showPassword)}
                                                               onMouseDown={event => {event.preventDefault()}}
                                                               edge="end"
                                                           >
                                                               {showPassword ? <Visibility/> : <VisibilityOff/>}
                                                           </IconButton>
                                                   }}/>
                                    </Grid>
                                    <Grid item xs={12}>
                                        {/*確認新密碼*/}
                                        <TextField name='confirmNewPassword' label='Enter the new password again.' variant='outlined' fullWidth required type={showConfirmPassword? "text": "password"}
                                                   InputProps={{
                                                       endAdornment:
                                                           <IconButton
                                                               aria-label="toggle password visibility"
                                                               onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                               onMouseDown={event => {event.preventDefault()}}
                                                               edge="end"
                                                           >
                                                               {showConfirmPassword ? <Visibility/> : <VisibilityOff/>}
                                                           </IconButton>
                                                   }}/>
                                    </Grid>
                                </Grid>

                            </Form>
                        </Grid>
                        <Grid item xs={6}>
                            <Button icon='save' text='OK' variant='contained' color='primary' fullWidth onClick={handleOK}/>
                        </Grid>
                        <Grid item xs={6}>
                            <Button icon='close' text='Cancel' variant='contained' color='primary' fullWidth onClick={props.onClose}/>
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>
        </Dialog>
    );
}

const withWorkbench = WrappedComponent => (props) => {
    return (
        <Workbench>
            <WrappedComponent {...props} />
        </Workbench>
    );
};

export default Workbench;

export { withWorkbench, WorkbenchContext };